import { api } from "./client";

/** «Операции и стоимость» склада и справочник тарифов
 *  (`backend/src/wh_helper/warehouse_billing/{ops,ops_service,rates,stock_ops,standing}.py`).
 *  Хранение и приёмка — из витрин DWH `mart.wh_*` (`mart.py`); отгрузки мимо
 *  портала — сверка проведения документов сборки с 1С (`postings.py`, `bypass.py`). */

export type OpsDirection = "mp" | "opt" | "fbs" | "undefined";

/** `set` — тариф действует; `unset` — на день не задан (суммы нет, это НЕ 0);
 *  `not_applicable` — у пары тарифа нет (направление не определено). */
export type TariffState = "set" | "unset" | "not_applicable";

export interface OpReason {
  reason: string;
  title: string;
  volume: number;
}

export interface OpRow {
  day: string;
  direction: OpsDirection;
  direction_title: string;
  operation: string;
  operation_title: string;
  unit: string;
  volume: number;
  tariff_key: string | null;
  tariff_title: string;
  tariff_value: number | null;
  tariff_valid_from: string | null;
  tariff_state: TariffState;
  amount: number | null;
  reasons: OpReason[];
  /** Справочная строка (вне категории ГП): объём виден, в счёт не идёт. */
  reference: boolean;
  /** Из чего сложен объём хранения/приёмки (коробы, норма, стоящие паллеты). */
  breakdown?: OpBreakdown | null;
}

export interface OpBreakdown {
  warehouses?: string[];
  /** Норма коробов на паллете на день; нет — паллеты по коробам не считались. */
  norm?: number;
  boxes?: number;
  /** Стоящие собранные паллеты, посчитанные фактом. */
  standing?: number;
  positions?: number;
  /** На стоящих паллетах больше, чем в остатке: столько штук вычесть не из чего. */
  shortfall_qty?: number;
  lines?: number;
  docs?: number;
}

export interface MartWarehouse {
  warehouse_ref: string;
  warehouse_name: string;
  direction: string;
  in_stock_mart: boolean;
  receipt_to: boolean;
  stock_date_from: string | null;
  stock_date_to: string | null;
  receipts_date_to: string | null;
  last_day_positions_no_box: number | null;
  last_day_qty_no_box: number | null;
}

/** Витрины склада DWH. `ok=false` — хранения и приёмки в отчёте НЕТ, `error` — почему. */
export interface MartStatus {
  ok: boolean;
  error: string | null;
  build_id: string | null;
  built_at: string | null;
  warehouses: MartWarehouse[];
  /** Дни периода без загруженного снимка остатка (все склады; сегодня — до сборки ~10:30). */
  missing_days: string[];
}

export interface StandingSummary {
  standing: number;
  no_signal: number;
  not_applied: number;
  shortfall_days: number;
  shortfall_qty: number;
  /** Из `standing` — конец по проведению документа в 1С (отгрузка мимо портала). */
  standing_by_posting: number;
  /** Из `standing` — заказ отгружен сменой портала, скан паллеты не совпал. */
  standing_by_portal_order: number;
  /** Дата проведённого документа раньше сборки: отгружены в день сборки, не стояли. */
  doc_before_assembly: number;
}

/** Сверка проведения документов сборки с 1С. `ok=false` — не шла / упала /
 *  устарела: «без сигнала» тогда может значить «не сверено». */
export interface PostingSync {
  ok: boolean;
  last_started_at: string | null;
  last_ok_at: string | null;
  last_error: string | null;
  stale: boolean;
  /** Последний прогон прошёл, но `failed` документов не сверены (ссылки — в `last_error`). */
  partial: boolean;
  failed: number;
  /** Последний прошедший прогон (полный или частичный). */
  last_done_at: string | null;
}

/** Не посчитано: сессии в другой базе 1С (реплика) — решение владельца «B».
 *  `known=false` — своя база ещё не известна, отделить нечем (сказано в `title`).
 *  `standing_pallets=null` — хранение не считалось (витрины недоступны). */
export interface Excluded {
  known: boolean;
  main_base: string | null;
  title: string;
  assembly_pallets: number;
  shipment_pallets: number;
  standing_pallets: number | null;
  sessions: string[];
  other_bases: string[];
}

export interface OpTotal {
  direction: OpsDirection;
  direction_title: string;
  operation: string;
  operation_title: string;
  unit: string;
  volume: number;
  /** Сумма строк С тарифом. */
  amount: number;
  /** Объём, оставшийся без суммы: итог полон, только когда здесь 0. */
  volume_unpriced: number;
  reasons: OpReason[];
  /** Справочная строка (вне категории ГП): «не считается», а не «без суммы». */
  reference: boolean;
}

export interface OpsReport {
  date_from: string;
  date_to: string;
  rows: OpRow[];
  totals: OpTotal[];
  notes: string[];
  mart: MartStatus;
  standing: StandingSummary | null;
  posting_sync: PostingSync | null;
  excluded: Excluded | null;
  /** Сверка «передано в DWH / сейчас по порталу» (`dwh_ops.py`). */
  dwh: DwhExport;
}

/** Статья, где переданное в DWH ≠ порталу сейчас. `null` объёма — строк этой
 *  статьи на стороне нет вовсе (не 0); `null` суммы — строки есть, суммы нет. */
export interface DwhOpsDiff {
  direction: string;
  direction_title: string;
  operation: string;
  operation_title: string;
  unit: string;
  dwh_volume: number | null;
  portal_volume: number | null;
  dwh_amount: number | null;
  portal_amount: number | null;
}

/** `not_exported` — витрин передачи в DWH ещё нет (не ошибка и не 0);
 *  `period_not_exported` — период целиком вне переданного (последняя передача кончилась
 *  до его начала или он кончился до `first_exported_day`);
 *  `error` — не прочитать / не согласованы; `ok` — сверены `compared_from…compared_to`. */
export type DwhExportState = "ok" | "not_exported" | "period_not_exported" | "error";

export interface DwhExport {
  state: DwhExportState;
  error: string | null;
  build_id: string | null;
  built_at: string | null;
  exported_from: string | null;
  exported_to: string | null;
  /** Первый день, когда-либо переданный в DWH (`mart.wh_ops_days`); есть всегда при
   *  `ok` / `period_not_exported` — дни раньше него не сверяются. */
  first_exported_day: string | null;
  computed_at_min: string | null;
  computed_at_max: string | null;
  compared_from: string | null;
  compared_to: string | null;
  dwh_amount: number | null;
  portal_amount: number | null;
  diffs: DwhOpsDiff[];
  complete: boolean | null;
  incomplete_reasons: string[];
  /** Портал сейчас без хранения и приёмки (витрины склада недоступны). */
  portal_partial: boolean;
}

/** Собранные паллеты и как ушли: через портал (скан; без скана паллеты — заказ отгружен
 *  сменой) / мимо (проведение в 1С) / без сигнала. */
export interface BypassCounts {
  assembled: number;
  scan: number;
  /** Через портал, но скан паллеты не совпал: смена провела документ её заказа. */
  portal_order: number;
  posting: number;
  /** Из `posting` — дата документа раньше сборки (отгружены в день сборки). */
  doc_before_assembly: number;
  none: number;
  /** Состояние документа → паллет без сигнала ("" — ещё не сверялся). */
  none_states: Record<string, number>;
  /** posting / (scan + portal_order + posting) × 100; отгрузок нет — null. */
  bypass_pct: number | null;
}

export interface BypassMonth extends BypassCounts {
  /** `YYYY-MM` — месяц первой сборки по Москве. */
  month: string;
}

export interface BypassGroup extends BypassCounts {
  month: string;
  direction: OpsDirection;
  direction_title: string;
  organization_name: string;
  kontragent_name: string;
}

export interface BypassReport {
  date_from: string;
  date_to: string;
  months: BypassMonth[];
  groups: BypassGroup[];
  posting_sync: PostingSync;
  state_titles: Record<string, string>;
  notes: string[];
  excluded: Excluded;
}

export type BypassSignal = "scan" | "portal_order" | "posting" | "none";

export interface BypassFilter {
  dateFrom: string;
  dateTo: string;
  month?: string;
  direction?: string;
  organization?: string;
  kontragent?: string;
  signal?: BypassSignal;
  /** Заголовок провала — что за числом. */
  title: string;
}

export interface BypassPallets {
  date_from: string;
  date_to: string;
  pallets: PalletInfo[];
}

export type PalletInfo = Record<string, string | number | boolean | null>;

export interface OpsPallets {
  date_from: string;
  date_to: string;
  standing: PalletInfo[];
  no_signal: PalletInfo[];
  not_applied: PalletInfo[];
  /** Дата документа раньше сборки — отгружены в день сборки. */
  doc_before_assembly: PalletInfo[];
  /** Сессии другой базы 1С — не считаются. */
  other_base: PalletInfo[];
}

export interface OpFact {
  day: string;
  at: string;
  direction: OpsDirection;
  operation: string;
  reason: string;
  reason_title: string;
  detail: Record<string, string | number | boolean | null>;
}

export interface OpsDetail {
  date_from: string;
  date_to: string;
  direction: OpsDirection;
  operation: string;
  facts: OpFact[];
}

export interface RateVersion {
  id: number;
  key: string;
  valid_from: string;
  value: number;
  note: string;
  created_by_name: string;
  created_at: string | null;
}

export interface RateKind {
  key: string;
  kind: "tariff" | "norm";
  title: string;
  unit: string;
  direction: string;
  /** Действует сегодня; `null` — «не задан» (не ноль). */
  current: RateVersion | null;
  versions: RateVersion[];
}

export interface RatesList {
  today: string;
  kinds: RateKind[];
}

export interface RateVersionIn {
  key: string;
  valid_from: string;
  /** Строкой: десятичное значение уходит как есть, без плавающей точки. */
  value: string;
  note: string;
}

/** Право правки тарифов — своё, не «Учёт операций склада». */
export const TARIFFS_EDIT_PERMISSION = "warehouse_tariffs";

export async function getOpsReport(dateFrom: string, dateTo: string): Promise<OpsReport> {
  const { data } = await api.get<OpsReport>("/warehouse-billing/ops", {
    params: { date_from: dateFrom, date_to: dateTo },
  });
  return data;
}

export async function getOpsDetail(params: {
  dateFrom: string;
  dateTo: string;
  direction: string;
  operation: string;
}): Promise<OpsDetail> {
  const { data } = await api.get<OpsDetail>("/warehouse-billing/ops/detail", {
    params: {
      date_from: params.dateFrom,
      date_to: params.dateTo,
      direction: params.direction,
      operation: params.operation,
    },
  });
  return data;
}

export async function getOpsPallets(dateFrom: string, dateTo: string): Promise<OpsPallets> {
  const { data } = await api.get<OpsPallets>("/warehouse-billing/ops/pallets", {
    params: { date_from: dateFrom, date_to: dateTo },
  });
  return data;
}

export async function getBypassReport(dateFrom: string, dateTo: string): Promise<BypassReport> {
  const { data } = await api.get<BypassReport>("/warehouse-billing/bypass", {
    params: { date_from: dateFrom, date_to: dateTo },
  });
  return data;
}

export async function getBypassPallets(f: BypassFilter): Promise<BypassPallets> {
  const { data } = await api.get<BypassPallets>("/warehouse-billing/bypass/pallets", {
    params: {
      date_from: f.dateFrom,
      date_to: f.dateTo,
      month: f.month,
      direction: f.direction,
      organization: f.organization,
      kontragent: f.kontragent,
      signal: f.signal,
    },
  });
  return data;
}

export async function getRates(): Promise<RatesList> {
  const { data } = await api.get<RatesList>("/warehouse-billing/rates");
  return data;
}

export async function createRate(body: RateVersionIn): Promise<RateVersion> {
  const { data } = await api.post<RateVersion>("/warehouse-billing/rates", body);
  return data;
}

export async function deleteRate(id: number): Promise<void> {
  await api.delete(`/warehouse-billing/rates/${id}`);
}
